import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Clock, Calendar, Award } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getTrainings } from '../utils/api';

const trainingCategories = [
  { name: 'MANAGEMENT', icon: '💼', color: 'blue' },
  { name: 'MARKETING', icon: '📢', color: 'purple' },
  { name: 'BUSINESS ADMIN', icon: '📊', color: 'green' },
  { name: 'COMPUTER', icon: '💻', color: 'indigo' },
  { name: 'PUBLIC HEALTH', icon: '🏥', color: 'red' },
  { name: 'SOCIAL WORKS', icon: '🤝', color: 'pink' },
  { name: 'SAFETY', icon: '🛡️', color: 'yellow' },
  { name: 'SECURITY', icon: '🔒', color: 'gray' },
  { name: 'INSURGENCY', icon: '⚠️', color: 'orange' },
  { name: 'ACCOUNTANCY', icon: '📈', color: 'teal' },
  { name: 'CREATIVITY', icon: '🎨', color: 'rose' },
  { name: 'COMMUNICATION SKILLS', icon: '💬', color: 'cyan' }
];

export const Trainings = () => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    loadTrainings();
  }, [selectedCategory]);

  const loadTrainings = async () => {
    try {
      const response = await getTrainings(selectedCategory);
      setTrainings(response.data);
    } catch (error) {
      console.error('Error loading trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    if (selectedCategory === category) {
      setSelectedCategory(null); // Clear filter
    } else {
      setSelectedCategory(category);
    }
    setLoading(true);
  };

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-br from-green-600 via-green-700 to-teal-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <GraduationCap className="h-20 w-20 mx-auto mb-6 text-green-200" />
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6" style={{fontFamily: 'Work Sans'}}>
            Professional Training Programs
          </h1>
          <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed">
            Excellence across 12 specialized disciplines • Over 8,000 professionals trained • Industry-recognized certifications
          </p>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-br from-green-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Training Categories</h2>
            <p className="text-xl text-gray-600">Choose your path to professional excellence</p>
            {selectedCategory && (
              <p className="mt-4 text-lg text-green-700 font-semibold">
                Filtered by: {selectedCategory} <button onClick={() => handleCategoryClick(selectedCategory)} className="ml-2 text-red-600 underline">Clear</button>
              </p>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trainingCategories.map((cat, idx) => (
              <div key={idx} onClick={() => handleCategoryClick(cat.name)} className="cursor-pointer">
                <Card className={`group hover:shadow-2xl transition-all duration-300 border-t-4 hover:scale-105 ${selectedCategory === cat.name ? 'ring-4 ring-green-500' : ''}`} style={{borderTopColor: cat.color === 'blue' ? '#3b82f6' : cat.color === 'green' ? '#10b981' : '#8b5cf6'}}>
                  <CardContent className="p-6 text-center">
                    <div className="text-5xl mb-4 group-hover:scale-125 transition-transform">{cat.icon}</div>
                    <h3 className="font-bold text-lg">{cat.name}</h3>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Available Programs</h2>
            <p className="text-xl text-gray-600">Comprehensive training with flexible scheduling</p>
          </div>

          {loading ? (
            <div className="text-center py-20"><p className="text-xl text-gray-600">Loading programs...</p></div>
          ) : trainings.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">No training programs found for this category.</p>
              {selectedCategory && (
                <button onClick={() => handleCategoryClick(selectedCategory)} className="mt-4 text-green-700 underline">Clear filter</button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {trainings.map(training => (
                <Card key={training.id} className="group hover:shadow-xl transition-all duration-300">
                  <CardHeader className="bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-t-xl">
                    <CardTitle className="text-xl">{training.title}</CardTitle>
                    <CardDescription className="text-green-100">{training.category}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p className="text-gray-600 mb-6 line-clamp-3">{training.description}</p>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2 text-green-600" />
                        <strong className="mr-2">Duration:</strong> {training.duration}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2 text-green-600" />
                        <strong className="mr-2">Schedule:</strong> {training.schedule}
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t">
                      <span className="text-2xl font-bold text-green-700">₦{training.fee.toLocaleString()}</span>
                      <Link to={`/trainings/${training.id}`}>
                        <Button className="group-hover:bg-green-700">View Details</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-green-600 to-teal-700 text-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Award className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-6">Why Choose Nigerland Training?</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { title: 'Expert Instructors', desc: '20+ years of combined industry experience' },
              { title: 'Flexible Learning', desc: 'Weekend and evening classes available' },
              { title: 'Certification', desc: 'Industry-recognized certificates upon completion' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-green-100">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};